import React, {useEffect, useState, useCallback} from 'react';
import {DialogContent, DialogActions, CircularProgress, Box} from '@mui/material';

import {TreeView, TreeItem} from '@mui/x-tree-view';
import FolderIcon from '@mui/icons-material/Folder';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {isNull} from 'lodash';
import Dialog from './Dialog';
import Button from 'components/buttons/Button';
import {useTranslation} from 'react-i18next';
import {useExpandToPath} from 'hooks/useExpandToPath';

type FileItem = {
   path: string;
   name: string;
   type: 'file' | 'folder';
};

type Props = {
   open: boolean;
   onClose: () => void;
   onSelect: (path: string) => void;
   currentPath?: string;
   fetchFolder: (path: string) => Promise<FileItem[]>;
};

export const MoveDialog: React.FC<Props> = ({
   open,
   onClose,
   onSelect,
   fetchFolder,
   currentPath,
}) => {
   const {t} = useTranslation();

   const [tree, setTree] = useState<Record<string, FileItem[] | null>>({});
   const [loading, setLoading] = useState<Record<string, boolean>>({});
   const [expanded, setExpanded] = useState<string[]>([]);
   const [selected, setSelected] = useState<string>('/');

   /**
    * Load folder children (only folders)
    */
   const loadFolder = useCallback(
      async (path: string) => {
         setLoading((prev) => {
            if (prev[path]) return prev;
            return {...prev, [path]: true};
         });

         setTree((prev) => {
            if (prev[path]) return prev;
            return prev;
         });

         try {
            const data = await fetchFolder(path);

            const folders = data ? data.filter((item) => item.type === 'folder') : null;

            setTree((prev) => ({
               ...prev,
               [path]: folders,
            }));
         } finally {
            setLoading((prev) => ({...prev, [path]: false}));
         }
      },
      [fetchFolder]
   );

   /**
    * Handle expand (lazy load)
    */
   const handleToggle = (_: any, nodeIds: string[]) => {
      console.debug('toggle', _, nodeIds);
      const newNodes = nodeIds.filter((id) => !expanded.includes(id));

      setExpanded(nodeIds);

      newNodes.forEach((id) => {
         loadFolder(id);
      });
   };

   useExpandToPath({
      open,
      currentPath,
      loadFolder,
      setExpanded,
      setSelected,
   });

   // /**
   //  * Initial load
   //  */
   // useEffect(() => {
   //    if (open) {
   //       loadFolder('/');
   //       setExpanded(['/']);
   //    }
   // }, [open]);

   /**
    * Recursive render
    */
   const renderTree = (path: string, name: string) => {
      const children = tree[path];

      return (
         <TreeItem
            key={path}
            nodeId={path}
            label={
               <Box display="flex" alignItems="center" gap={1}>
                  <FolderIcon fontSize="small" />
                  {name}
                  {loading[path] && <CircularProgress size={12} />}
               </Box>
            }
         >
            {children ? (
               children.map((child) => renderTree(child.path, child.name))
            ) : isNull(children) ? undefined : (
               <CircularProgress size={12} />
            )}
         </TreeItem>
      );
   };

   return (
      <Dialog open={open} close={onClose} header={t('Move to folder')} fullWidth maxWidth="sm">
         <DialogContent dividers>
            <TreeView
               defaultCollapseIcon={<ExpandMoreIcon />}
               defaultExpandIcon={<ChevronRightIcon />}
               expanded={expanded}
               selected={selected}
               onNodeToggle={handleToggle}
               onNodeSelect={(_, nodeId) => setSelected(nodeId)}
            >
               {renderTree('/', 'Root')}
            </TreeView>
         </DialogContent>

         <DialogActions>
            <Button
               color="primary"
               onClick={() => {
                  onSelect(selected);
                  onClose();
               }}
            >
               Move here
            </Button>
            <Button onClick={onClose} color="inherit">
               Cancel
            </Button>
         </DialogActions>
      </Dialog>
   );
};
